// @ts-nocheck
"use client";

import * as React from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ExternalLink, X, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils"; // Adjusted import path

export type CardStackItem = {
    id: string | number;
    title: string;
    description?: string;
    imageSrc?: string;
    href?: string;
    ctaLabel?: string;
    tag?: string;
};

export type CardStackProps<T extends CardStackItem> = {
    items: T[];

    /** Selected index on mount */
    initialIndex?: number;

    /** How many cards are visible around the active (odd recommended) */
    maxVisible?: number;

    /** Card sizing */
    cardWidth?: number;
    cardHeight?: number;

    /** How much cards overlap each other (0..0.8). Higher = more overlap */
    overlap?: number;

    /** Total fan angle (deg). Higher = wider arc */
    spreadDeg?: number;

    /** 3D / depth feel */
    perspectivePx?: number;
    depthPx?: number;
    tiltXDeg?: number;

    /** Active emphasis */
    activeLiftPx?: number;
    activeScale?: number;
    inactiveScale?: number;

    /** Motion */
    springStiffness?: number;
    springDamping?: number;

    /** Behavior */
    loop?: boolean;
    autoAdvance?: boolean;
    intervalMs?: number;
    pauseOnHover?: boolean;

    /** UI */
    showDots?: boolean;
    className?: string;

    /** Hooks */
    onChangeIndex?: (index: number, item: T) => void;

    /** Custom renderer (optional) */
    renderCard?: (item: T, state: { active: boolean }) => React.ReactNode;
};

function wrapIndex(n: number, len: number) {
    if (len <= 0) return 0;
    return ((n % len) + len) % len;
}

/** Minimal signed offset from active index to i, with wrapping (for loop behavior). */
function signedOffset(i: number, active: number, len: number, loop: boolean) {
    const raw = i - active;
    if (!loop || len <= 1) return raw;

    // consider wrapped alternative
    const alt = raw > 0 ? raw - len : raw + len;
    return Math.abs(alt) < Math.abs(raw) ? alt : raw;
}

// ─── Lightbox ──────────────────────────────────────────────────────────────

function Lightbox<T extends CardStackItem>({
    items,
    startIndex,
    onClose,
}: {
    items: T[];
    startIndex: number;
    onClose: () => void;
}) {
    const [idx, setIdx] = React.useState(startIndex);
    const item = items[idx]!;
    const len = items.length;

    const goPrev = React.useCallback(() => setIdx((i) => wrapIndex(i - 1, len)), [len]);
    const goNext = React.useCallback(() => setIdx((i) => wrapIndex(i + 1, len)), [len]);

    // keyboard
    React.useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowLeft") goPrev();
            if (e.key === "ArrowRight") goNext();
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onClose, goPrev, goNext]);

    // lock body scroll while open and hide navbar
    React.useEffect(() => {
        // Add class to <html> — the CSS rule for this class has higher specificity
        // than the !important media-query overflow:auto in index.css
        document.documentElement.classList.add('lightbox-open');

        // Hide navbar
        window.dispatchEvent(new CustomEvent('toggleHeaderVisibility', { detail: { hidden: true } }));

        return () => {
            document.documentElement.classList.remove('lightbox-open');
            // Show navbar
            window.dispatchEvent(new CustomEvent('toggleHeaderVisibility', { detail: { hidden: false } }));
        };
    }, []);

    return (
        <motion.div
            data-lightbox="true"
            className="fixed inset-0 z-[9999] flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            style={{ touchAction: "pan-y" }}
        >
            {/* Backdrop */}
            <motion.div
                className="absolute inset-0 bg-black/90 backdrop-blur-md"
                onClick={onClose}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
            />

            {/* Modal card */}
            <motion.div
                className="relative z-10 mx-4 flex flex-col overflow-hidden rounded-2xl bg-gray-900 shadow-2xl border border-white/10"
                style={{ maxWidth: 820, width: "100%", maxHeight: "90vh" }}
                initial={{ scale: 0.85, opacity: 0, y: 32 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.85, opacity: 0, y: 32 }}
                transition={{ type: "spring", stiffness: 320, damping: 30 }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close button */}
                <button
                    onClick={onClose}
                    className="absolute top-3 right-3 z-20 flex items-center justify-center w-9 h-9 rounded-full bg-black/60 text-white hover:bg-black/90 transition"
                    aria-label="Close"
                >
                    <X size={18} />
                </button>

                {/* Image area */}
                <div className="relative w-full overflow-hidden" style={{ maxHeight: "60vh" }}>
                    <AnimatePresence mode="wait" initial={false}>
                        <motion.img
                            key={item.id}
                            src={item.imageSrc}
                            alt={item.title}
                            className="w-full object-contain bg-black"
                            style={{ maxHeight: "60vh", display: "block" }}
                            draggable={false}
                            initial={{ opacity: 0, x: 40 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -40 }}
                            transition={{ duration: 0.22 }}
                        />
                    </AnimatePresence>

                    {/* Gradient overlay at bottom of image */}
                    <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-gray-900 to-transparent" />

                    {/* Prev / Next arrows over image */}
                    <button
                        onClick={goPrev}
                        className="absolute left-3 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-10 h-10 rounded-full bg-black/60 text-white hover:bg-black/90 transition"
                        aria-label="Previous"
                    >
                        <ChevronLeft size={20} />
                    </button>
                    <button
                        onClick={goNext}
                        className="absolute right-3 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-10 h-10 rounded-full bg-black/60 text-white hover:bg-black/90 transition"
                        aria-label="Next"
                    >
                        <ChevronRight size={20} />
                    </button>
                </div>

                {/* Details */}
                <div className="flex flex-col gap-3 px-6 py-5">
                    <AnimatePresence mode="wait" initial={false}>
                        <motion.div
                            key={item.id + "-text"}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                        >
                            <h2
                                className="text-2xl font-bold text-white mb-2"
                                style={{ fontFamily: "'Audiowide', cursive" }}
                            >
                                {item.title}
                            </h2>
                            {item.description && (
                                <p className="text-gray-300 text-sm leading-relaxed">
                                    {item.description}
                                </p>
                            )}
                            {item.href && (
                                <a
                                    href={item.href}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition"
                                >
                                    <ExternalLink size={14} /> Learn more
                                </a>
                            )}
                        </motion.div>
                    </AnimatePresence>


                </div>
            </motion.div>
        </motion.div>
    );
}

// ─── Main CardStack ────────────────────────────────────────────────────────

export function CardStack<T extends CardStackItem>({
    items,
    initialIndex = 0,
    maxVisible = 7,

    cardWidth = 520,
    cardHeight = 320,

    overlap = 0.48,
    spreadDeg = 48,

    perspectivePx = 1100,
    depthPx = 140,
    tiltXDeg = 12,

    activeLiftPx = 22,
    activeScale = 1.03,
    inactiveScale = 0.94,

    springStiffness = 280,
    springDamping = 28,

    loop = true,
    autoAdvance = false,
    intervalMs = 2800,
    pauseOnHover = true,

    showDots = false,
    className,

    onChangeIndex,
    renderCard,
}: CardStackProps<T>) {
    const reduceMotion = useReducedMotion();
    const len = items.length;

    const [active, setActive] = React.useState(() =>
        wrapIndex(initialIndex, len),
    );
    const [hovering, setHovering] = React.useState(false);

    // Lightbox state
    const [lightboxIndex, setLightboxIndex] = React.useState<number | null>(null);

    // Responsive: measure container width and scale card dimensions
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = React.useState(cardWidth);

    React.useEffect(() => {
        if (!containerRef.current) return;
        const obs = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setContainerWidth(entry.contentRect.width);
            }
        });
        obs.observe(containerRef.current);
        return () => obs.disconnect();
    }, []);

    // On small screens clamp the card so it fits inside the container with padding
    const effectiveCardWidth = Math.min(cardWidth, containerWidth - 32);
    const effectiveCardHeight = Math.round(
        cardHeight * (effectiveCardWidth / cardWidth)
    );

    // keep active in bounds if items change
    React.useEffect(() => {
        setActive((a) => wrapIndex(a, len));
    }, [len]);

    React.useEffect(() => {
        if (!len) return;
        onChangeIndex?.(active, items[active]!);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    const maxOffset = Math.max(0, Math.floor(maxVisible / 2));

    const cardSpacing = Math.max(10, Math.round(effectiveCardWidth * (1 - overlap)));
    const stepDeg = maxOffset > 0 ? spreadDeg / maxOffset : 0;

    const canGoPrev = loop || active > 0;
    const canGoNext = loop || active < len - 1;

    const prev = React.useCallback(() => {
        if (!len) return;
        if (!canGoPrev) return;
        setActive((a) => wrapIndex(a - 1, len));
    }, [canGoPrev, len]);

    const next = React.useCallback(() => {
        if (!len) return;
        if (!canGoNext) return;
        setActive((a) => wrapIndex(a + 1, len));
    }, [canGoNext, len]);

    // keyboard navigation (when container focused)
    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "ArrowLeft") prev();
        if (e.key === "ArrowRight") next();
    };

    // autoplay
    React.useEffect(() => {
        if (!autoAdvance) return;
        if (reduceMotion) return;
        if (!len) return;
        if (pauseOnHover && hovering) return;
        if (lightboxIndex !== null) return; // pause when lightbox is open

        const id = window.setInterval(
            () => {
                if (loop || active < len - 1) next();
            },
            Math.max(700, intervalMs),
        );

        return () => window.clearInterval(id);
    }, [
        autoAdvance,
        intervalMs,
        hovering,
        pauseOnHover,
        reduceMotion,
        len,
        loop,
        active,
        next,
        lightboxIndex,
    ]);

    if (!len) return null;

    const activeItem = items[active]!;

    return (
        <>
            <div
                ref={containerRef}
                className={cn("w-full", className)}
                onMouseEnter={() => setHovering(true)}
                onMouseLeave={() => setHovering(false)}
            >
                {/* Stage */}
                <div
                    className="relative w-full outline-none"
                    style={{ height: Math.max(280, effectiveCardHeight + 80) }}
                    tabIndex={0}
                    onKeyDown={onKeyDown}
                >
                    {/* background wash / spotlight (unique feel) */}
                    <div
                        className="pointer-events-none absolute inset-x-0 top-6 mx-auto h-48 w-[70%] rounded-full bg-blue-500/10 blur-3xl"
                        aria-hidden="true"
                    />
                    <div
                        className="pointer-events-none absolute inset-x-0 bottom-0 mx-auto h-40 w-[76%] rounded-full bg-purple-500/10 blur-3xl"
                        aria-hidden="true"
                    />

                    <div
                        className="absolute inset-0 flex items-end justify-center"
                        style={{
                            perspective: `${perspectivePx}px`,
                        }}
                    >
                        <AnimatePresence initial={false}>
                            {items.map((item, i) => {
                                const off = signedOffset(i, active, len, loop);
                                const abs = Math.abs(off);
                                const visible = abs <= maxOffset;

                                if (!visible) return null;

                                const rotateZ = off * stepDeg;
                                const x = off * cardSpacing;
                                const y = abs * 10;
                                const z = -abs * depthPx;

                                const isActive = off === 0;
                                const scale = isActive ? activeScale : inactiveScale;
                                const lift = isActive ? -activeLiftPx : 0;
                                const rotateX = isActive ? 0 : tiltXDeg;
                                const zIndex = 100 - abs;

                                // drag only on the active card
                                const dragProps = isActive
                                    ? {
                                        drag: "x" as const,
                                        dragConstraints: { left: 0, right: 0 },
                                        dragElastic: 0.18,
                                        onDragEnd: (
                                            _e: any,
                                            info: { offset: { x: number }; velocity: { x: number } },
                                        ) => {
                                            if (reduceMotion) return;
                                            const travel = info.offset.x;
                                            const v = info.velocity.x;
                                            const threshold = Math.min(160, effectiveCardWidth * 0.22);
                                            if (travel > threshold || v > 650) prev();
                                            else if (travel < -threshold || v < -650) next();
                                        },
                                    }
                                    : {};

                                return (
                                    <motion.div
                                        key={item.id}
                                        className={cn(
                                            "absolute bottom-0 rounded-2xl border-4 border-white/10 overflow-hidden shadow-2xl",
                                            "will-change-transform select-none bg-gray-900",
                                            "cursor-pointer",
                                        )}
                                        style={{
                                            width: effectiveCardWidth,
                                            height: effectiveCardHeight,
                                            zIndex,
                                            transformStyle: "preserve-3d",
                                            touchAction: isActive ? "none" : "pan-y",
                                        }}
                                        initial={
                                            reduceMotion
                                                ? false
                                                : {
                                                    opacity: 0,
                                                    y: y + 40,
                                                    x,
                                                    rotateZ,
                                                    rotateX,
                                                    scale,
                                                }
                                        }
                                        animate={{
                                            opacity: 1,
                                            x,
                                            y: y + lift,
                                            rotateZ,
                                            rotateX,
                                            scale,
                                        }}
                                        transition={{
                                            type: "spring",
                                            stiffness: springStiffness,
                                            damping: springDamping,
                                        }}
                                        onClick={() => {
                                            // If inactive card: first click switches, second click (or if already active) opens lightbox
                                            if (!isActive) {
                                                setActive(i);
                                            } else {
                                                setLightboxIndex(i);
                                            }
                                        }}
                                        {...dragProps}
                                    >
                                        {/* "Tap to expand" hint on active card */}
                                        {isActive && (
                                            <div className="absolute top-2 right-2 z-20 flex items-center gap-1 px-2 py-1 rounded-full bg-black/50 text-white/70 text-xs pointer-events-none select-none">
                                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" /></svg>
                                                Tap to expand
                                            </div>
                                        )}
                                        <div
                                            className="h-full w-full"
                                            style={{
                                                transform: `translateZ(${z}px)`,
                                                transformStyle: "preserve-3d",
                                            }}
                                        >
                                            {renderCard ? (
                                                renderCard(item, { active: isActive })
                                            ) : (
                                                <DefaultFanCard item={item} active={isActive} />
                                            )}
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </AnimatePresence>
                    </div>
                </div>

                {/* Dots navigation */}
                {showDots ? (
                    <div className="mt-6 flex items-center justify-center gap-3">
                        <div className="flex items-center gap-2">
                            {items.map((it, idx) => {
                                const on = idx === active;
                                return (
                                    <button
                                        key={it.id}
                                        onClick={() => setActive(idx)}
                                        className={cn(
                                            "h-2 w-2 rounded-full transition-all duration-300",
                                            on
                                                ? "bg-blue-500 w-6"
                                                : "bg-gray-500/50 hover:bg-gray-400/80",
                                        )}
                                        aria-label={`Go to ${it.title}`}
                                    />
                                );
                            })}
                        </div>
                        {activeItem.href ? (
                            <a
                                href={activeItem.href}
                                target="_blank"
                                rel="noreferrer"
                                className="text-gray-400 hover:text-white transition"
                                aria-label="Open link"
                            >
                                <ExternalLink className="h-4 w-4" />
                            </a>
                        ) : null}
                    </div>
                ) : null}
            </div>

            {/* Lightbox Portal */}
            <AnimatePresence>
                {lightboxIndex !== null && (
                    <Lightbox
                        items={items}
                        startIndex={lightboxIndex}
                        onClose={() => setLightboxIndex(null)}
                    />
                )}
            </AnimatePresence>
        </>
    );
}

function DefaultFanCard({ item }: { item: CardStackItem; active: boolean }) {
    return (
        <div className="relative h-full w-full bg-gray-900 text-white">
            {/* image */}
            <div className="absolute inset-0">
                {item.imageSrc ? (
                    <img
                        src={item.imageSrc}
                        alt={item.title}
                        className="h-full w-full object-cover"
                        draggable={false}
                        loading="eager"
                    />
                ) : (
                    <div className="flex h-full w-full items-center justify-center bg-gray-800 text-sm text-gray-400">
                        No image
                    </div>
                )}
            </div>

            {/* subtle gradient overlay at bottom for text readability */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

            {/* content */}
            <div className="relative z-10 flex h-full flex-col justify-end p-6">
                <h3 className="truncate text-xl font-bold text-white mb-2">
                    {item.title}
                </h3>
                {item.description ? (
                    <p className="line-clamp-2 text-sm text-gray-200">
                        {item.description}
                    </p>
                ) : null}
            </div>
        </div>
    );
}
